import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Hook: real-time listener for the current user's verification application
 */
export const useMyVerificationApplication = (uid) => {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setApplication(null);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'verification_applications'),
      where('userId', '==', uid)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.empty) {
          setApplication(null);
        } else {
          const doc = snapshot.docs[0];
          setApplication({ id: doc.id, ...doc.data() });
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching verification application:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [uid]);

  return { application, loading };
};

/**
 * Hook: real-time listener for all verification applications (admin)
 */
export const useAllVerificationApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'verification_applications'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setApplications(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching verification applications:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  return { applications, loading };
};

/**
 * Submit a verification application
 */
export const createVerificationApplication = async ({
  realFullName,
  bio,
  contactEmail,
  phoneNumber,
  userId,
  userEmail,
  userAnonymizedUsername,
}) => {
  const docRef = await addDoc(collection(db, 'verification_applications'), {
    userId,
    userEmail,
    userAnonymizedUsername,
    realFullName,
    bio,
    contactEmail,
    phoneNumber,
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Update user doc with pending status
  await updateDoc(doc(db, 'users', userId), {
    verificationStatus: 'pending',
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
};

/**
 * Approve a verification application (admin)
 */
export const approveVerification = async (applicationId, userId) => {
  await updateDoc(doc(db, 'verification_applications', applicationId), {
    status: 'approved',
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await updateDoc(doc(db, 'users', userId), {
    verificationStatus: 'verified',
    verifiedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Reject a verification application (admin)
 */
export const rejectVerification = async (applicationId, userId, rejectionReason) => {
  await updateDoc(doc(db, 'verification_applications', applicationId), {
    status: 'rejected',
    rejectionReason,
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await updateDoc(doc(db, 'users', userId), {
    verificationStatus: 'rejected',
    updatedAt: serverTimestamp(),
  });
};
